import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, IconButton, Select, MenuItem, FormControl, Divider, ToggleButton, ToggleButtonGroup, Menu
} from '@mui/material';
import { 
  ArrowCounterClockwise, ArrowClockwise, TextB, TextItalic, TextUnderline, 
  TextAlignLeft, TextAlignCenter, TextAlignRight, Hash, Image, Palette, Minus, Plus
} from '@phosphor-icons/react';
import { useAuthStore } from '../../../store/storeAuth';

export interface FormattingState {
  fontFamily: string;
  fontSize: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  textAlign: string;
  textColor: string;
  backgroundColor: string;
}

export interface FormattingToolbarProps {
  show: boolean;
  position: { top: number; left: number };
  formatting: FormattingState;
  onFormattingChange: (formatting: Partial<FormattingState>) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onInsertPageNumber?: () => void;
  onInsertImage?: () => void;
  className?: string;
  showInsertOptions?: boolean;
}

const defaultFormatting: FormattingState = {
  fontFamily: 'Arial',
  fontSize: '12',
  bold: false,
  italic: false,
  underline: false,
  textAlign: 'left',
  textColor: '#000000',
  backgroundColor: '#ffffff'
};

const commonColors = [
  '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
  '#800000', '#008000', '#000080', '#808000', '#800080', '#008080', '#C0C0C0', '#808080'
];

export default function FormattingToolbar({ 
  show, 
  position, 
  formatting, 
  onFormattingChange,
  onUndo,
  onRedo,
  onInsertPageNumber,
  onInsertImage,
  className = '',
  showInsertOptions = true
}: FormattingToolbarProps) {
  const { user } = useAuthStore(state => state);
  const [colorMenuAnchor, setColorMenuAnchor] = useState<null | HTMLElement>(null);
  const [backgroundColorMenuAnchor, setBackgroundColorMenuAnchor] = useState<null | HTMLElement>(null);

  if (!show || !position) return null;

  const handleFormattingChange = (key: keyof FormattingState, value: any) => {
    onFormattingChange({ [key]: value });
  };

  const handleBold = () => {
    handleFormattingChange('bold', !formatting.bold);
  };

  const handleItalic = () => {
    handleFormattingChange('italic', !formatting.italic);
  };

  const handleUnderline = () => {
    handleFormattingChange('underline', !formatting.underline);
  };

  const handleAlignment = (alignment: string) => {
    if (alignment) handleFormattingChange('textAlign', alignment);
  };

  return (
    <Box 
      className={`formatting-toolbar ${className}`}
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      sx={{ 
        position: 'absolute',
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        bgcolor: user?.theme === 'dark' ? '#344054' : '#FFFFFF',
        border: `1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'}`,
        borderRadius: '8px',
        p: 1,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        zIndex: 9999,
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        minWidth: '600px',
      }}
    >
      {(onUndo || onRedo) && (
        <>
          {onUndo && (
            <IconButton size="small" onClick={onUndo} sx={{ color: user?.theme === 'dark' ? '#D0D5DD' : '#101828' }}>
              <ArrowCounterClockwise size={16} />
            </IconButton>
          )}
          {onRedo && (
            <IconButton size="small" onClick={onRedo} sx={{ color: user?.theme === 'dark' ? '#D0D5DD' : '#101828' }}>
              <ArrowClockwise size={16} />
            </IconButton>
          )}
        </>
      )}
      
      <FormControl size="small" sx={{ minWidth: 80 }}>
        <Select
          value={formatting.fontFamily}
          onChange={(e) => handleFormattingChange('fontFamily', e.target.value)}
          onMouseDown={(e) => e.stopPropagation()}
          onBlur={(e) => e.stopPropagation()}
          MenuProps={{
            onMouseDown: (e) => e.stopPropagation(),
          }}
          sx={{ 
            fontSize: '13px',
            bgcolor: user?.theme === 'dark' ? '#475467' : '#F3F4F6',
            border: 'none',
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none'
            },
            '& .MuiSelect-select': {
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              py: 0.5,
              px: 1
            }
          }}
        >
          <MenuItem value="Arial">Arial</MenuItem>
          <MenuItem value="Times New Roman">Times New Roman</MenuItem>
          <MenuItem value="Helvetica">Helvetica</MenuItem>
          <MenuItem value="Georgia">Georgia</MenuItem>
          <MenuItem value="Verdana">Verdana</MenuItem>
          <MenuItem value="Roboto">Roboto</MenuItem>
          <MenuItem value="Inter">Inter</MenuItem>
        </Select>
      </FormControl>
      
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: `1px solid ${user?.theme === 'dark' ? '#475467' : '#D0D5DD'}`,
          borderRadius: '20px',
          overflow: 'hidden',
          minWidth: '90px',
          height: '24px',
        }}
      >
        <IconButton
          size="small"
          onClick={() => {
            const currentSize = parseInt(formatting.fontSize);
            const newSize = Math.max(8, currentSize - 1);
            handleFormattingChange('fontSize', newSize.toString());
          }}
          sx={{
            width: '26px',
            height: '26px',
            borderRadius: 0,
            color: user?.theme === 'dark' ? '#D0D5DD' : '#52525B',
            '&:hover': {
              bgcolor: user?.theme === 'dark' ? '#475467' : '#E5E7EB',
            },
            '& svg': {
              color: user?.theme === 'dark' ? '#D0D5DD' : '#52525B',
            },
          }}
        >
          <Minus size={16} />
        </IconButton>
        
        <Box
          sx={{
            flex: 1,
            textAlign: 'center',
            fontSize: '13px',
            color: user?.theme === 'dark' ? '#D0D5DD' : '#52525B',
            fontWeight: 500,
            px: 1,
            py: 0.5,
          }}
        >
          {formatting.fontSize}
        </Box>
        
        <IconButton
          size="small"
          onClick={() => {
            const currentSize = parseInt(formatting.fontSize);
            const newSize = Math.min(72, currentSize + 1);
            handleFormattingChange('fontSize', newSize.toString());
          }}
          sx={{
            width: '26px',
            height: '26px',
            borderRadius: 0,
            color: user?.theme === 'dark' ? '#D0D5DD' : '#52525B',
            '&:hover': {
              bgcolor: user?.theme === 'dark' ? '#475467' : '#E5E7EB',
            },
            '& svg': {
              color: user?.theme === 'dark' ? '#D0D5DD' : '#52525B',
            },
          }}
        >
          <Plus size={16} />
        </IconButton>
      </Box>
      
      <Divider orientation="vertical" sx={{ mx: 0.5, height: '24px' }} />
      
      <ToggleButtonGroup 
        size="small" 
        value={[formatting.bold && 'bold', formatting.italic && 'italic', formatting.underline && 'underline'].filter(Boolean)}
        sx={{
          '& .MuiToggleButton-root': {
            border: 'none',
            color: user?.theme === 'dark' ? '#D0D5DD' : '#52525B',
            '&:hover': {
              bgcolor: user?.theme === 'dark' ? '#475467' : '#E5E7EB',
            },
            '&.Mui-selected': {
              bgcolor: user?.theme === 'dark' ? '#475467' : '#E5E7EB',
              color: user?.theme === 'dark' ? '#D0D5DD' : '#52525B',
            },
          },
        }}
      >
        <ToggleButton value="bold" onClick={handleBold}>
          <TextB size={18} weight="bold" />
        </ToggleButton>
        <ToggleButton value="italic" onClick={handleItalic}>
          <TextItalic size={18} weight="bold" />
        </ToggleButton>
        <ToggleButton value="underline" onClick={handleUnderline}>
          <TextUnderline size={18} weight="bold" />
        </ToggleButton>
      </ToggleButtonGroup>
      
      <Divider orientation="vertical" sx={{ mx: 0.5, height: '24px' }} />
      
      <IconButton 
        size="small" 
        onClick={(e) => setColorMenuAnchor(e.currentTarget)}
        sx={{ color: formatting.textColor }}
      >
        <Palette size={16} />
      </IconButton>
      
      <ToggleButtonGroup size="small" value={formatting.textAlign} exclusive onChange={(e, value) => handleAlignment(value)}>
        <ToggleButton value="left">
          <TextAlignLeft size={16} />
        </ToggleButton>
        <ToggleButton value="center">
          <TextAlignCenter size={16} />
        </ToggleButton>
        <ToggleButton value="right">
          <TextAlignRight size={16} />
        </ToggleButton>
      </ToggleButtonGroup>
      
      <Divider orientation="vertical" sx={{ mx: 0.5, height: '24px' }} />
      
      <IconButton 
        size="small" 
        onClick={(e) => setBackgroundColorMenuAnchor(e.currentTarget)}
        sx={{ 
          color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
          bgcolor: formatting.backgroundColor !== '#ffffff' ? formatting.backgroundColor : 'transparent'
        }}
      >
        <Box sx={{ width: 16, height: 16, bgcolor: formatting.backgroundColor, border: '1px solid #ccc' }} />
      </IconButton>
      
      {showInsertOptions && (
        <>
          {onInsertPageNumber && (
            <IconButton size="small" onClick={onInsertPageNumber} sx={{ color: user?.theme === 'dark' ? '#D0D5DD' : '#101828' }}>
              <Hash size={16} />
            </IconButton>
          )}
          
          {onInsertImage && (
            <IconButton size="small" onClick={onInsertImage} sx={{ color: user?.theme === 'dark' ? '#D0D5DD' : '#101828' }}>
              <Image size={16} />
            </IconButton>
          )}
        </>
      )}
      
      <Menu
        anchorEl={colorMenuAnchor}
        open={Boolean(colorMenuAnchor)}
        onClose={() => setColorMenuAnchor(null)}
      >
        <Box sx={{ p: 1, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5, width: 120 }}>
          {commonColors.map(color => (
            <Box
              key={color}
              sx={{
                width: 24,
                height: 24,
                bgcolor: color,
                border: '1px solid #ccc',
                cursor: 'pointer',
                '&:hover': { transform: 'scale(1.1)' }
              }}
              onClick={() => {
                handleFormattingChange('textColor', color);
                setColorMenuAnchor(null);
              }}
            />
          ))}
        </Box>
      </Menu>
      
      <Menu
        anchorEl={backgroundColorMenuAnchor}
        open={Boolean(backgroundColorMenuAnchor)}
        onClose={() => setBackgroundColorMenuAnchor(null)}
      >
        <Box sx={{ p: 1, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5, width: 120 }}>
          {commonColors.map(color => (
            <Box
              key={color}
              sx={{
                width: 24,
                height: 24,
                bgcolor: color,
                border: '1px solid #ccc',
                cursor: 'pointer',
                '&:hover': { transform: 'scale(1.1)' }
              }}
              onClick={() => {
                handleFormattingChange('backgroundColor', color);
                setBackgroundColorMenuAnchor(null);
              }}
            />
          ))}
        </Box>
      </Menu>
    </Box>
  );
}

export { defaultFormatting }; 